import * as React from "react";
import { ScrollCarousel } from "~/components/carousel/ScrollCarousel";
import {
  CardTextContentReview,
  type CardTextContentReviewClassNames,
} from "~/components/ui/card-text-content-review";
import { HighlightCard } from "~/components/ui/highlight-card";
import { SectionTitle } from "~/components/ui/section-title";
import {
  firstReviewImageUrl,
  formatReviewMonth,
  type ProductReview,
} from "~/lib/reviews";

export type ReviewCard =
  | {
      type: "text";
      quote: string;
      name: string;
      subtitle: string;
      avatarUrl?: string | null;
      rating?: number;
    }
  | { type: "photo"; src: string; position?: string };

export interface SocialProofItem {
  key: string;
  // The whole per-store block (icon, rating, stars, count). See storeRatingItems
  // in ./StoreRatings.
  content: React.ReactNode;
}

interface ReviewsCarouselProps {
  id?: string;
  eyebrow?: React.ReactNode;
  headline: React.ReactNode;
  socialProof: SocialProofItem[];
  cards: ReviewCard[];
  ariaLabel: string;
}

// One card size and gap at every breakpoint.
const CARD_SIZE = { width: 326, height: 349 };
const CARD_GAP = 24;

// Compact scale at every size: 16px quote, Body/S Bold 14/20 name, Body/XS Regular
// 12/18 subtitle. No avatar per Figma's updated card (node 10479:112249) — hidden
// rather than removed from CardTextContentReview, which other callers still render with one.
const CARD_CLASS_NAMES: CardTextContentReviewClassNames = {
  // Root gap covers stars→name (8px, per design); quote→stars needs 24px, made up by
  // mt-4 below — these two gaps are different sizes, not the uniform 24/24 a single gap gives.
  root: "gap-2",
  quote: "text-text-body-secondary text-base leading-[1.5]",
  stars: "mt-4 gap-[3px]",
  reviewer: "items-start",
  avatar: "hidden",
  avatarFallback: "text-xs",
  details: "gap-0 pt-px",
  name: "text-text-body-alt text-sm leading-[20px] font-bold",
  // Same color as name (text-body-alt) in Figma — only weight differs, not shade.
  subtitle: "text-text-body-alt text-xs leading-[18px]",
};

interface ReviewCardLabels {
  anonymousReviewer: string;
  language: string;
}

/** Maps Yotpo reviews to carousel cards: photo reviews become photo cards. */
export function reviewCardsFrom(
  reviews: ProductReview[],
  labels: ReviewCardLabels,
): ReviewCard[] {
  return reviews.flatMap((review): ReviewCard[] => {
    const imageUrl = firstReviewImageUrl(review);
    if (imageUrl !== null) return [{ type: "photo", src: imageUrl }];

    // A Yotpo review can be score-only. With neither a body nor a photo there is
    // nothing to show, so those are dropped rather than rendered as an empty card.
    const quote = review.content === null ? "" : review.content.trim();
    if (quote === "") return [];

    const subtitle = formatReviewMonth(review.createdAt, labels.language) ?? "";

    return [
      {
        type: "text",
        quote,
        name: review.reviewer.displayName ?? labels.anonymousReviewer,
        subtitle,
        avatarUrl: review.reviewer.avatarUrl,
        rating: review.score,
      },
    ];
  });
}

// Two reviews, then a photo, which makes every 3rd carousel slot an image. The
// counter runs over the review list, so inserted photos can't shift later ones.
const REVIEWS_PER_STOCK_PHOTO = 2;

export interface StockPhoto {
  src: string;
  /** CSS object-position, for a photo whose focal point isn't centered. */
  position?: string;
}

/** Splices a stock photo in after every second review, cycling through `photos`. */
export function withStockPhotos(
  cards: ReviewCard[],
  photos: readonly StockPhoto[],
): ReviewCard[] {
  if (photos.length === 0) return cards;
  return cards.flatMap((card, i): ReviewCard[] => {
    if ((i + 1) % REVIEWS_PER_STOCK_PHOTO !== 0) return [card];
    const photo =
      photos[Math.floor(i / REVIEWS_PER_STOCK_PHOTO) % photos.length];
    return [card, { type: "photo", src: photo.src, position: photo.position }];
  });
}

export function ReviewsCarousel({
  id,
  eyebrow,
  headline,
  socialProof,
  cards,
  ariaLabel,
}: ReviewsCarouselProps) {
  return (
    <section id={id} className="bg-bg-alt scroll-mt-20 py-10 md:py-15">
      {eyebrow && (
        <p className="wrap eyebrow mb-2 text-center md:mb-3">{eyebrow}</p>
      )}
      <SectionTitle className="wrap text-text-heading mb-6 text-center">
        {headline}
      </SectionTitle>

      {socialProof.length > 0 && (
        <div className="wrap mb-8 flex flex-row items-start justify-center gap-8 md:mb-[29px] md:gap-[46px]">
          {socialProof.map((item) => (
            <React.Fragment key={item.key}>{item.content}</React.Fragment>
          ))}
        </div>
      )}

      {cards.length > 0 && (
        <ScrollCarousel
          cardWidth={CARD_SIZE.width}
          cardGap={CARD_GAP}
          ariaLabel={ariaLabel}
          prevVariant="secondary"
          // Nav sits 24px under the cards (the scroller's 4px shadow padding is pulled
          // back), with #949BA8 disabled arrows and a 0.5px divider.
          navClassName="-mt-1 [&_button:disabled]:text-neutral-300"
          trackClassName="h-[0.5px] bg-neutral-300 md:bg-border"
        >
          {cards.map((card, i) => (
            <div key={i} className="shrink-0" style={CARD_SIZE}>
              {card.type === "photo" ? (
                <HighlightCard
                  useCase="imagery"
                  image={card.src}
                  imagePosition={card.position}
                  style={CARD_SIZE}
                />
              ) : (
                <HighlightCard useCase="color" fill="white" style={CARD_SIZE}>
                  <CardTextContentReview
                    quote={card.quote}
                    name={card.name}
                    subtitle={card.subtitle}
                    avatarUrl={card.avatarUrl}
                    rating={card.rating}
                    starColor="text-bouncie-500"
                    classNames={CARD_CLASS_NAMES}
                    compact
                  />
                </HighlightCard>
              )}
            </div>
          ))}
        </ScrollCarousel>
      )}
    </section>
  );
}
